'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { InfoIcon } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

const platformpayFormSchema = z.object({
  userId: z.string().min(1, 'User ID is required').trim(),
  secretKey: z.string().min(1, 'Secret key is required').trim(),
})

type FormValues = z.infer<typeof platformpayFormSchema>

interface PlatformPayFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

export function PlatformPayForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: PlatformPayFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(platformpayFormSchema),
    defaultValues: {
      userId: defaultValues.credentials?.userId ?? '',
      secretKey: defaultValues.credentials?.secretKey ?? '',
    },
  })

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit({
      credentials: {
        userId: data.userId,
        secretKey: data.secretKey,
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>How it works</AlertTitle>
        <AlertDescription>
          After each successful payment, order, customer and card-summary data is
          sent to your PlatformPay.io Live Data Feed. PlatformPay does not process
          the payment — it only receives the completed-order report. Find your User
          ID and Secret Key in the PlatformPay console.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>API Credentials</CardTitle>
          <CardDescription>
            Credentials from your PlatformPay.io account (console.platformpay.io).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId">
              User ID
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="userId"
              type="text"
              placeholder="229"
              disabled={isLoading}
              {...form.register('userId')}
            />
            {form.formState.errors.userId && (
              <p className="text-xs text-destructive">{form.formState.errors.userId.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="secretKey">
              Secret Key
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="secretKey"
              type="password"
              placeholder="Your PlatformPay secret key"
              autoComplete="off"
              disabled={isLoading}
              {...form.register('secretKey')}
            />
            {form.formState.errors.secretKey && (
              <p className="text-xs text-destructive">{form.formState.errors.secretKey.message}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
